import React, { useState, useEffect } from 'react';
import { Star, Send, CheckCircle, Clock, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config';

const texts = {
  title: { sv: 'Vad våra elever säger', en: 'What Our Students Say', ar: 'آراء طلابنا', tr: 'Öğrencilerimiz ne diyor', fa: 'نظرات دانش‌آموزان ما', so: 'Waxay yidhaahdaan ardaydayada', pl: 'Co mówią nasi uczniowie', es: 'Lo que dicen nuestros estudiantes', de: 'Was unsere Schüler sagen' },
  subtitle: { sv: 'Verkliga upplevelser från elever som klarat körprovet', en: 'Real experiences from students who passed their driving test', ar: 'تجارب حقيقية من طلاب نجحوا في اختبار القيادة', tr: 'Sürüş sınavını geçen öğrencilerin gerçek deneyimleri', fa: 'تجربیات واقعی دانش‌آموزانی که آزمون رانندگی را قبول شدند', so: 'Waayo-aragnimada dhabta ah ee ardayda ka gudbay imtixaanka darawalnimada', pl: 'Prawdziwe doświadczenia uczniów, którzy zdali egzamin', es: 'Experiencias reales de estudiantes que aprobaron su examen', de: 'Echte Erfahrungen von Schülern, die ihre Prüfung bestanden haben' },
  writeReview: { sv: 'Skriv en recension', en: 'Write a Review', ar: 'اكتب تقييمك', tr: 'Yorum yaz', fa: 'نظر بنویسید', so: 'Qor dib-u-eegis', pl: 'Napisz recenzję', es: 'Escribir una reseña', de: 'Bewertung schreiben' },
  rating: { sv: 'Betyg', en: 'Rating', ar: 'التقييم', tr: 'Puan', fa: 'امتیاز', so: 'Qiimayn', pl: 'Ocena', es: 'Calificación', de: 'Bewertung' },
  comment: { sv: 'Din kommentar', en: 'Your comment', ar: 'تعليقك', tr: 'Yorumunuz', fa: 'نظر شما', so: 'Faalladaada', pl: 'Twój komentarz', es: 'Tu comentario', de: 'Dein Kommentar' },
  commentPlaceholder: { sv: 'Dela din upplevelse med DriveMaster...', en: 'Share your experience with DriveMaster...', ar: 'شاركنا تجربتك مع DriveMaster...', tr: 'DriveMaster deneyiminizi paylaşın...', fa: 'تجربه خود با DriveMaster را به اشتراک بگذارید...', so: 'La wadaag khibraddaada DriveMaster...', pl: 'Podziel się swoim doświadczeniem z DriveMaster...', es: 'Comparte tu experiencia con DriveMaster...', de: 'Teile deine Erfahrung mit DriveMaster...' },
  submit: { sv: 'Skicka recension', en: 'Submit Review', ar: 'إرسال التقييم', tr: 'Yorumu gönder', fa: 'ارسال نظر', so: 'Dir dib-u-eegiska', pl: 'Wyślij recenzję', es: 'Enviar reseña', de: 'Bewertung senden' },
  pending: { sv: 'Din recension väntar på godkännande', en: 'Your review is pending approval', ar: 'تقييمك قيد المراجعة', tr: 'Yorumunuz onay bekliyor', fa: 'نظر شما در انتظار تأیید است', so: 'Dib-u-eegiskaagu wuxuu sugayaa ogolaansho', pl: 'Twoja recenzja czeka na zatwierdzenie', es: 'Tu reseña está pendiente de aprobación', de: 'Deine Bewertung wartet auf Genehmigung' },
  alreadyReviewed: { sv: 'Du har redan skickat en recension', en: 'You already submitted a review', ar: 'لقد أرسلت تقييمك بالفعل', tr: 'Zaten bir yorum gönderdiniz', fa: 'شما قبلاً نظر ارسال کرده‌اید', so: 'Horta waad soo dirtay dib-u-eegis', pl: 'Już wysłałeś recenzję', es: 'Ya enviaste una reseña', de: 'Du hast bereits eine Bewertung abgegeben' },
  loginToReview: { sv: 'Logga in för att skriva en recension', en: 'Log in to write a review', ar: 'سجل دخولك لكتابة تقييم', tr: 'Yorum yazmak için giriş yapın', fa: 'برای نوشتن نظر وارد شوید', so: 'Gal si aad u qorto dib-u-eegis', pl: 'Zaloguj się, aby napisać recenzję', es: 'Inicia sesión para escribir una reseña', de: 'Melde dich an, um eine Bewertung zu schreiben' },
  success: { sv: 'Tack! Din recension har publicerats', en: 'Thanks! Your review has been published', ar: 'شكراً! تم نشر تقييمك', tr: 'Teşekkürler! Yorumunuz yayınlandı', fa: 'ممنون! نظر شما منتشر شد', so: 'Mahadsanid! Dib-u-eegiskaagii waa la daabacay', pl: 'Dziękujemy! Twoja recenzja została opublikowana', es: '¡Gracias! Tu reseña ha sido publicada', de: 'Danke! Deine Bewertung wurde veröffentlicht' },
  noReviews: { sv: 'Inga recensioner än. Bli den första!', en: 'No reviews yet. Be the first!', ar: 'لا توجد تقييمات بعد. كن أول من يقيّم!', tr: 'Henüz yorum yok. İlk siz olun!', fa: 'هنوز نظری ثبت نشده. اولین نفر باشید!', so: 'Wali ma jiraan dib-u-eegis. Noqo kii ugu horreeyay!', pl: 'Brak recenzji. Bądź pierwszy!', es: '¡Aún no hay reseñas. Sé el primero!', de: 'Noch keine Bewertungen. Sei der Erste!' },
  seeAll: { sv: 'Se alla recensioner', en: 'See All Reviews', ar: 'عرض جميع التقييمات', tr: 'Tüm yorumları gör', fa: 'مشاهده همه نظرات', so: 'Arag dhammaan dib-u-eegisyada', pl: 'Zobacz wszystkie recenzje', es: 'Ver todas las reseñas', de: 'Alle Bewertungen ansehen' },
  reviews: { sv: 'recensioner', en: 'reviews', ar: 'تقييم', tr: 'yorum', fa: 'نظر', so: 'dib-u-eegis', pl: 'recenzji', es: 'reseñas', de: 'Bewertungen' },
  ownerReply: { sv: 'Svar från DriveMaster', en: 'Reply from DriveMaster', ar: 'تم الرد من قبل DriveMaster', tr: 'DriveMaster yanıtı', fa: 'پاسخ از طرف DriveMaster', so: 'Jawaab ka timid DriveMaster', pl: 'Odpowiedź od DriveMaster', es: 'Respuesta de DriveMaster', de: 'Antwort von DriveMaster' },
  verified: { sv: 'Verifierat företag', en: 'Verified business', ar: 'حساب موثّق', tr: 'Doğrulanmış işletme', fa: 'کسب‌وکار تأییدشده', so: 'Ganacsi la xaqiijiyay', pl: 'Zweryfikowana firma', es: 'Empresa verificada', de: 'Verifiziertes Unternehmen' },
  login: { sv: 'Logga in', en: 'Log in', ar: 'تسجيل الدخول', tr: 'Giriş yap', fa: 'ورود', so: 'Gal', pl: 'Zaloguj się', es: 'Iniciar sesión', de: 'Anmelden' },
};

const t = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const StarRating = ({ rating, onRate, interactive = false, size = 'w-5 h-5' }) => (
  <div className="flex gap-0.5" role="img" aria-label={`Rating: ${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(star => (
      <span
        key={star}
        role={interactive ? 'button' : 'presentation'}
        tabIndex={interactive ? 0 : undefined}
        onClick={() => interactive && onRate?.(star)}
        onKeyDown={(e) => interactive && e.key === 'Enter' && onRate?.(star)}
        className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}
        aria-label={interactive ? `Rate ${star} stars` : undefined}
      >
        <Star
          className={`${size} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      </span>
    ))}
  </div>
);

const getComment = (comment, lang) => {
  if (typeof comment === 'object' && comment !== null) {
    return comment[lang] || comment['en'] || comment['sv'] || Object.values(comment)[0] || '';
  }
  return comment || '';
};

export const OwnerReply = ({ reply, lang, compact = false }) => {
  if (!reply) return null;
  return (
    <div
      className="mt-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900/50 p-3 border-s-4 border-s-sky-500"
      data-testid="review-owner-reply"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <BadgeCheck className="w-4 h-4 text-white fill-sky-500 flex-shrink-0" aria-label={t('verified', lang)} />
        <span className="text-xs font-bold text-sky-700 dark:text-sky-300" data-testid="review-owner-reply-label">{t('ownerReply', lang)}</span>
        {!compact && <span className="text-[10px] text-sky-600/70 dark:text-sky-400/70">· {t('verified', lang)}</span>}
      </div>
      <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line break-words">
        {getComment(reply.text, lang)}
      </p>
    </div>
  );
};

const ReviewCard = ({ review, lang, onClick }) => (
  <div 
    className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow cursor-pointer active:scale-[0.98]" 
    data-testid="review-card"
    onClick={onClick}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center font-bold text-emerald-700 dark:text-emerald-400 text-sm">
          {review.user_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div>
          <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{review.user_name}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {review.created_at ? new Date(review.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : lang === 'sv' ? 'sv-SE' : 'en-US') : ''}
          </p>
        </div>
      </div>
      <StarRating rating={review.rating} size="w-4 h-4" />
    </div>
    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed line-clamp-3">{getComment(review.comment, lang)}</p>
    <OwnerReply reply={review.admin_reply} lang={lang} compact />
  </div>
);

// قسم التقييمات للصفحة الرئيسية
export const ReviewsSection = ({ onSeeAll }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [selectedReview, setSelectedReview] = useState(null);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews/public?limit=50`)
      .then(res => {
        setReviews(res.data.reviews || []);
        setStats({ average_rating: res.data.average_rating, total_reviews: res.data.total_reviews });
      })
      .catch(() => {});
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section className="py-16 bg-slate-50 dark:bg-slate-900/50" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4 border border-transparent dark:border-amber-800/30">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            {stats.average_rating} / 5 ({stats.total_reviews} {t('reviews', lang)})
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {t('title', lang)}
          </h2>
          <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mb-6">
            {t('subtitle', lang)}
          </p>
          {/* Scroll arrows in header */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: -350, behavior: 'smooth' })}
              className="w-11 h-11 rounded-full bg-white dark:bg-slate-700 border-2 border-emerald-500 shadow-lg flex items-center justify-center hover:bg-emerald-50 dark:hover:bg-slate-600 transition-colors"
              aria-label="Scroll left"
              data-testid="reviews-scroll-left"
            >
              <ChevronLeft className="w-6 h-6 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
            </button>
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: 350, behavior: 'smooth' })}
              className="w-11 h-11 rounded-full bg-white dark:bg-slate-700 border-2 border-emerald-500 shadow-lg flex items-center justify-center hover:bg-emerald-50 dark:hover:bg-slate-600 transition-colors"
              aria-label="Scroll right"
              data-testid="reviews-scroll-right"
            >
              <ChevronRight className="w-6 h-6 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>

      <div>
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scroll-smooth px-4 sm:px-6 lg:px-[calc((100%-1280px)/2+2rem)] pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {reviews.map(review => (
            <div key={review.id} className="min-w-[280px] max-w-[320px] flex-shrink-0 snap-start">
              <ReviewCard review={review} lang={lang} onClick={() => setSelectedReview(review)} />
            </div>
          ))}
        </div>
      </div>

      {onSeeAll && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" onClick={onSeeAll} className="rounded-full px-6 border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" data-testid="write-review-landing-btn">
            <Star className="w-4 h-4 me-2" />
            {t('writeReview', lang)}
          </Button>
        </div>
      )}

      {/* مودال عرض التقييم الكامل */}
      {selectedReview && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedReview(null)}
          data-testid="review-modal-overlay"
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="review-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center font-bold text-emerald-700 dark:text-emerald-400 text-lg">
                  {selectedReview.user_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">{selectedReview.user_name}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    {selectedReview.created_at ? new Date(selectedReview.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : lang === 'sv' ? 'sv-SE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedReview(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-slate-500 dark:text-slate-400"
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
              <StarRating rating={selectedReview.rating} size="w-5 h-5" />
            </div>
            <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-base">
              {getComment(selectedReview.comment, lang)}
            </p>
            <OwnerReply reply={selectedReview.admin_reply} lang={lang} />
          </div>
        </div>
      )}
    </section>
  );
};

// نموذج كتابة تقييم
export const ReviewForm = ({ onSubmitted }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const { isAuthenticated, token } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0 || comment.length < 5) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { rating, comment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
      toast.success(t('success', lang));
      onSubmitted?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error';
      if (msg.includes('already')) {
        toast.info(t('alreadyReviewed', lang));
        setSubmitted(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const navigate = useNavigate();

  if (!isAuthenticated) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm text-center" data-testid="review-login-prompt">
        <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-4">
          <Star className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-2">{t('writeReview', lang)}</h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">{t('loginToReview', lang)}</p>
        <Button
          onClick={() => navigate('/logga-in')}
          className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 w-full"
          data-testid="login-to-review-btn"
        >
          {t('login', lang)}
        </Button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-2 justify-center py-6 text-emerald-600" data-testid="review-submitted">
        <Clock className="w-5 h-5" />
        <p className="font-medium">{t('pending', lang)}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="review-form">
      <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4">{t('writeReview', lang)}</h3>
      
      <div className="mb-4">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">{t('rating', lang)}</label>
        <StarRating rating={rating} onRate={setRating} interactive size="w-7 h-7" />
      </div>

      <div className="mb-4">
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder={t('commentPlaceholder', lang)}
          rows={3}
          className="w-full rounded-xl border border-slate-200 dark:border-slate-600 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          data-testid="review-comment-input"
        />
      </div>

      <Button
        type="submit"
        disabled={rating === 0 || comment.length < 5 || submitting}
        className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-6"
        data-testid="submit-review-btn"
      >
        <Send className="w-4 h-4 me-2" />
        {t('submit', lang)}
      </Button>
    </form>
  );
};

export default ReviewCard;
